// Formatação que o usuário lê. Em pt-BR, sempre.

/**
 * Data como um cliente de e-mail mostra: hora para hoje, dia da semana para
 * esta semana, data para o resto.
 *
 * Mostrar "14/09/2026 09:42" em tudo desperdiça a coluna mais estreita da
 * lista com o que o usuário já sabe — que a mensagem chegou hoje.
 */
export function dataCurta(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";

  const agora = new Date();
  const mesmoDia =
    d.getDate() === agora.getDate() &&
    d.getMonth() === agora.getMonth() &&
    d.getFullYear() === agora.getFullYear();

  if (mesmoDia) {
    return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  }
  const dias = (agora.getTime() - d.getTime()) / 86_400_000;
  if (dias < 7 && dias >= 0) {
    return d.toLocaleDateString("pt-BR", { weekday: "short" }).replace(".", "");
  }
  if (d.getFullYear() === agora.getFullYear()) {
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" }).replace(".", "");
  }
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

/**
 * Uma data no formato que `<input type="date">` fala: `aaaa-mm-dd`.
 *
 * `toISOString().slice(0, 10)` é o atalho óbvio e está ERRADO em todo fuso
 * negativo — o ISO é UTC, então das 21h em diante (UTC-3) ele devolve o dia
 * de AMANHÃ. Num cartão "Recusadas hoje" que leva à lista filtrada por data,
 * isso abriria a lista num dia que ainda não começou: zero linhas, com o
 * número do cartão dizendo doze.
 *
 * Daí os campos LOCAIS, que são os mesmos que a pessoa vê no relógio.
 */
export function dataParaCampo(d: Date): string {
  const mes = String(d.getMonth() + 1).padStart(2, "0");
  const dia = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mes}-${dia}`;
}

/** Data por extenso, para o cabeçalho da mensagem aberta. */
export function dataLonga(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("pt-BR", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function tamanho(bytes: number | undefined | null): string {
  // Zero CONHECIDO é diferente de tamanho desconhecido. Devolver vazio nos
  // dois casos fazia a barra de cota exibir "de 2,0 GB", sem dizer de quanto.
  if (bytes === undefined || bytes === null) return "";
  if (bytes === 0) return "0 B";
  const unidades = ["B", "KB", "MB", "GB"];
  let n = bytes;
  let i = 0;
  while (n >= 1024 && i < unidades.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0).replace(".", ",")} ${unidades[i]}`;
}

/**
 * Nome curto para a coluna do remetente.
 *
 * Aceita `null` porque é o que o contrato devolve em `remetente_nome`,
 * `assunto` e `preview`. Tipar como `string | undefined` compilaria com um
 * cast e produziria "null" escrito na tela.
 *
 * Quem tem nome de exibição aparece por ele; quem não tem, pela parte local do
 * endereço. Mostrar "nao-responda@mkt.exemplo.com.br" inteiro come metade da
 * linha para dizer o que "nao-responda" já disse.
 */
export function nomeCurto(nome: string | null | undefined, endereco: string | null | undefined): string {
  if (nome && nome.trim()) return nome.trim();
  if (!endereco) return "";
  const arroba = endereco.indexOf("@");
  return arroba > 0 ? endereco.slice(0, arroba) : endereco;
}

/** Inicial para o avatar. Nunca vazia: um círculo em branco parece defeito. */
export function inicial(nome: string | null | undefined, endereco: string | null | undefined): string {
  const base = nomeCurto(nome, endereco);
  const letra = base.trim()[0];
  return letra ? letra.toUpperCase() : "?";
}

/**
 * Cor do avatar derivada do endereço.
 *
 * Determinística de propósito: a mesma pessoa tem sempre a mesma cor, em
 * qualquer sessão e qualquer máquina. Cor sorteada a cada render faria a lista
 * piscar e destruiria o reconhecimento visual que o avatar existe para dar.
 */
const CORES_AVATAR = [
  "#0f766e", "#4f46e5", "#b45309", "#be123c",
  "#6d28d9", "#0369a1", "#15803d", "#a16207",
];

export function corDoAvatar(endereco: string | null | undefined): string {
  if (!endereco) return CORES_AVATAR[0]!;
  let soma = 0;
  for (let i = 0; i < endereco.length; i++) soma = (soma * 31 + endereco.charCodeAt(i)) >>> 0;
  return CORES_AVATAR[soma % CORES_AVATAR.length]!;
}
